import { useLayoutEffect, useRef, useSyncExternalStore, type ReactElement } from "react";
import { logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { slotRect } from "../hud/area-slots.ts";
import type { HudArea } from "../hud/slots.ts";
import type { HarvestSnapshot } from "./store.ts";

/**
 * What the bar reads of the harvest store.
 */
export interface HudHarvest {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => HarvestSnapshot;
}

/**
 * The harvest hold's bar in the HUD's cast slot (ADR-142 decision 3, P2.26-T3), drawn as the travel channel's is (the
 * design's `CastBar`, a visual reference): "Harvesting" on the track with the seconds left at its right, the brass fill
 * growing to full over the hold's ticks, and on a break the bar full in the danger's dark fill reading "Harvest
 * interrupted" a moment. The fill and the seconds are written through refs only when the tenth of a second shown
 * changes, and the frames stop when the hold's time is up.
 *
 * @param props The `area` it is laid out on and the `harvest` store it reads.
 * @returns The bar, or nothing while no hold runs.
 */
export function HarvestBar({ area, harvest }: { readonly area: HudArea; readonly harvest: HudHarvest }): ReactElement | null {
  const snapshot = useSyncExternalStore(harvest.subscribe, harvest.getSnapshot, harvest.getSnapshot);
  const fill = useRef<HTMLDivElement>(null);
  const seconds = useRef<HTMLSpanElement>(null);
  const bar = snapshot.bar;

  useLayoutEffect(() => {
    if (bar.kind !== "hold") {
      return undefined;
    }
    let frame = 0;
    let shownTenths = -1;
    const span = Math.max(1, bar.endsMs - bar.startedMs);
    const step = (): void => {
      const left = Math.max(0, bar.endsMs - performance.now());
      const tenths = Math.ceil(left / 100);
      if (tenths !== shownTenths) {
        shownTenths = tenths;
        if (fill.current !== null) {
          fill.current.style.width = `${Math.min(100, ((span - tenths * 100) / span) * 100)}%`;
        }
        if (seconds.current !== null) {
          seconds.current.textContent = `${(tenths / 10).toFixed(1)}s`;
        }
      }
      frame = left > 0 ? requestAnimationFrame(step) : 0;
    };
    step();
    return () => cancelAnimationFrame(frame);
  }, [bar]);

  if (bar.kind === "none") {
    return null;
  }
  const rect = slotRect("cast", area);
  const interrupted = bar.kind === "interrupted";
  const words = interrupted ? say("harvest.interrupted") : say("harvest.channel");
  return (
    <div className="ot-hud-slot" data-hud-slot="cast" style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <div className={interrupted ? "ot-travel-bar is-interrupted" : "ot-travel-bar"} data-hud="harvest" data-state={bar.kind} role="progressbar" aria-label={words}>
        <div key={bar.kind} ref={fill} className="ot-travel-bar__fill" />
        <span className="ot-travel-bar__words">
          <span>{words}</span>
          {interrupted ? null : <span ref={seconds} />}
        </span>
      </div>
    </div>
  );
}
